import { Input, Select, Spin, Table } from "antd";
import React, { useEffect, useState } from "react";
import { useFilePreview } from "../../completed/ReportingList/Download";
import Preview from "../../planning/VisitBoard/TableView/modals/Preview";
import previewIcon from "./../../../../Assets/svg/table-action-preview.svg";
import SearchIcon from "./../../../../Assets/svg/searchIcon.svg";
import { httpService } from "../../../../services/httpService.service";
import {
  formatDate,
  formatDateAndTime,
} from "../../../../services/helpFunctions/formatDateAndTime";
import FacilityTag from "../../../components/FacilityTag/FacilityTag";
import { REPORTING_DEFAULT_COLUMNS } from "../../../components/SiteVisitColumns/siteVisitColumnOrder";
import { useSiteVisitColumns } from "../../../components/SiteVisitColumns/useSiteVisitColumns";
const { Option } = Select;

const SubmittedForApproval = ({ loading, setLoading, setMessage }) => {
  const { previewFiles } = useFilePreview();
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [siteVisitCardsWaitingApproval, setSiteVisitCardsWaitingApproval] =
    useState([]);
  const [searchQueryForApproval, setSearchQueryForApproval] = useState("");
  const [debouncedQueryForApproval, setDebouncedQueryForApproval] = useState(
    searchQueryForApproval
  );
  const [paginationForApproval, setPaginationForApproval] = useState({
    pageSize: 10,
    pageNumber: 1,
    total: 0,
  });
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSearchForApproval = (e) => {
    setSearchQueryForApproval(e.target.value);
    setPaginationForApproval((prevPagination) => ({
      ...prevPagination,
      pageNumber: 1,
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQueryForApproval(searchQueryForApproval);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQueryForApproval]);

  const fetchSiteVisitsWaitingApproval = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const params = {
      ProjectId: projectId,
      Search: debouncedQueryForApproval,
      pageSize: paginationForApproval.pageSize,
      pageNumber: paginationForApproval.pageNumber,
      ApprovalStatus: 1,
    };

    const filteredParams = Object.keys(params).reduce((acc, key) => {
      if (params[key] !== undefined && params[key] !== null) {
        acc[key] = params[key];
      }
      return acc;
    }, {});

    const queryString = new URLSearchParams(filteredParams).toString();

    const apiUrl = `/api/Site/getAllSiteVisits?${queryString}`;
    setLoading(true);
    httpService
      .get(
        apiUrl,
        (res) => {
          setSiteVisitCardsWaitingApproval(res.data);
          setPaginationForApproval({
            pageSize: res.metaData.pageSize,
            pageNumber: res.metaData.pageNumber,
            total: res.metaData.totalItemCount,
          });
        },
        (error) => {
          // console.log(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSiteVisitsWaitingApproval();
  }, [
    paginationForApproval.pageNumber,
    paginationForApproval.pageSize,
    debouncedQueryForApproval,
  ]);

  const columnsWaitingApproval = [
    {
      title: <span className="styledTitle text-nowrap">Approval Status</span>,
      dataIndex: "approvalStatus",
      width: 150,
      key: "approvalStatus",
      render: () => {
        return (
          <div className="documentReview">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill="#FC9403" />
            </svg>
            <span>Pending</span>
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle">Site Visit Identifier</span>,
      sorter: (a, b) =>
        a.siteVisitIdentifier.localeCompare(b.siteVisitIdentifier),
      width: 170,
      dataIndex: "siteVisitIdentifier",
      key: "siteVisitIdentifier",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
      ellipsis: true,
    },
    {
      title: <span className="styledTitle">Site Visit Name</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 170,
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      width: 250,
      sorter: (a, b) =>
        (a.facility?.name || "").localeCompare(b.facility?.name || ""),
      render: (facility) => <FacilityTag facility={facility} />,
    },
    {
      title: <span className="styledTitle">Actual Dates of Visit</span>,
      sorter: (a, b) => {
        const aDate = a.actualDateOfVisit?.[0] || "";
        const bDate = b.actualDateOfVisit?.[0] || "";
        return aDate.localeCompare(bDate);
      },
      dataIndex: "actualDateOfVisit",
      ellipsis: true,
      width: 200,
      key: "actualDateOfVisit",
      render: (data) =>
        data?.length ? (
          data?.map((date) => (
            <span
              className="styledDataIndex3"
              style={{ display: "block" }}
              key={date.id}
            >
              {date}
            </span>
          ))
        ) : (
          <span className="styledDataIndex3">
            No actual date of visit available
          </span>
        ),
    },
    {
      title: <span className="styledTitle">Location</span>,
      sorter: (a, b) =>
        a.locationDetails?.country.localeCompare(b.locationDetails?.country),
      key: "location",
      dataIndex: "location",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Governate/State</span>,
      dataIndex: "state",
      key: "state",
      ellipsis: true,
      width: 150,
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">IP</span>,
      sorter: (a, b) => a.ip.localeCompare(b.ip),
      dataIndex: "ip",
      key: "ip",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Cluster</span>,
      sorter: (a, b) => a.cluster.localeCompare(b.cluster),
      dataIndex: "cluster",
      key: "cluster",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Virtual</span>,
      sorter: (a, b) => a.virtual.localeCompare(b.virtual),
      dataIndex: "virtual",
      key: "virtual",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Resources</span>,
      sorter: (a, b) =>
        a.resources?.join("").localeCompare(b.resources?.join("")),
      dataIndex: "resources",
      key: "resources",
      render: (resources) => {
        return (
          <div className="resourcesColumn">
            {resources?.map((file, index) => (
              <span
                onClick={(e) => previewFiles(e, file.fileKey, file.id)}
                key={index}
              >
                {file?.fileName}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle"></span>,
      key: "action",
      width: 120,
      fixed:
        isLargeScreen && siteVisitCardsWaitingApproval.length > 0
          ? "right"
          : undefined,
      render: (text, record) => (
        <div className="actionDiv">
          <img
            src={previewIcon}
            onClick={() => handleTogglePreviewModal(record)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
  ];

  const handleTogglePreviewModal = (record) => {
    const siteVisitCardId = record?.key;
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(!showPreviewModal);
  };

  const formatData = (siteVisitCard) => {
    return {
      key: siteVisitCard?.id,
      siteVisitIdentifier: siteVisitCard?.identifier,
      name: siteVisitCard?.name || "N/A",
      actualDateOfVisit: siteVisitCard?.tracking?.siteTrackingVisitDates.map(
        (date) => formatDate(date?.visitDate)
      ),
      ip: siteVisitCard?.ipPlan?.implementingPartner?.name,
      location: siteVisitCard?.locationDetails?.country,
      state:
        siteVisitCard?.locationDetails?.state || "This country has no states.",
      cluster: siteVisitCard?.cluster?.name,
      virtual: siteVisitCard?.isVirtual ? "Yes" : "No",
      resources: siteVisitCard?.siteVisitFiles.filter((item) =>
        [1, 3, 4, 5].includes(item.fileCategory)
      ),

      reportingYear: siteVisitCard?.tracking?.reportingYear,
      reportingMonth: siteVisitCard?.tracking?.reportingMonth,
      trainingDate: siteVisitCard?.tracking?.trainingDate,
      trainingScoreInfo: siteVisitCard?.tracking?.trainingScoreInfo,
      dateOfReport: siteVisitCard?.tracking?.dateOfReport,
      dateOfDebrief: siteVisitCard?.tracking?.dateOfDebrief,
      rejectionReason: siteVisitCard?.rejectionReason || null,
      percentageOfDataCollectionCompleted:
        siteVisitCard?.tracking?.percentageOfDataCollectionCompleted,
      fieldTeamReportingStatus:
        siteVisitCard?.tracking?.fieldTeamReportingStatus,
      trainingIsCompleted: siteVisitCard?.tracking?.trainingIsCompleted,
    };
  };

  const formattedDataWaitingApproval =
    siteVisitCardsWaitingApproval?.map(formatData);

  const {
    columns: visibleColumns,
    columnsBar,
    components: tableComponents,
  } = useSiteVisitColumns({
    tableId: "reportingListSubmittedForApproval",
    columns: columnsWaitingApproval,
    defaultKeys: REPORTING_DEFAULT_COLUMNS,
  });

  return (
    <>
      <div className="flagsPage mb-4">
        <span className="tableViewHeaderText">
          Submitted Site Visits for Approval
        </span>
      </div>
      <div className="filtersWrapper">
        <div className="containerFilters">
          <Input
            onChange={handleSearchForApproval}
            placeholder="Search... "
            suffix={<img src={SearchIcon} alt="Search" />}
          />
        </div>
      </div>
      <div
        className="w-100 my-3 d-flex justify-content-start gap-2"
        style={{
          color: "var(--2, #AA1A5F)",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: "600",
          lineHeight: "24px",
        }}
      >
        {loading ? (
          <Spin className="me-2" size="small" spinning={loading} />
        ) : (
          `Total: ${paginationForApproval.total} visits`
        )}
        {columnsBar && <span className="ms-auto">{columnsBar}</span>}
      </div>
      <Table
        components={tableComponents}
        rowKey="id"
        dataSource={formattedDataWaitingApproval}
        columns={
          formattedDataWaitingApproval?.length > 0 ? visibleColumns : []
        }
        loading={loading}
        className="tableTPM"
        pagination={{
          ...setPaginationForApproval,
          onChange: (page, pageSize) => {
            setPaginationForApproval({
              ...paginationForApproval,
              pageNumber: page,
              pageSize: pageSize,
            });
          },
        }}
        // Without this the table is capped at its container: it already ran off
        // the right edge with no scrollbar, and widening one column pushed two
        // more columns out of reach for good.
        scroll={{ x: "max-content" }}
      />
      <Preview
        onClose={handleTogglePreviewModal}
        visible={showPreviewModal}
        siteVisitCardId={selectedSiteVisit}
      />
    </>
  );
};

export default SubmittedForApproval;
